'use client';

import { ShareDialog } from "@/components/share-dialog";
import { getTailwindGradientColor, getUrl } from "@/lib/utils";
import { FeedbackDialog } from "@/components/feedback-dialog";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ChartBarSquareIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  MinusCircleIcon,
  XCircleIcon
} from "@heroicons/react/24/outline";
import { ScoreBar } from "@/components/ui/score-bar";
import { Badge } from "@/components/ui/badge";
import IsHelpful from "@/components/is-helpful";
import {getScoredEntityName, getScoreLabel, getTechniqueEmoji, getTechniqueUrl} from "@/lib/score";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import {ExternalLinkIcon, FileTextIcon} from "lucide-react";
import useAnalytics from "@/hooks/useAnalytics";

export type ReportContent = { [key: string]: any };

const Report = ({ content, pageSignature }: { content: ReportContent, pageSignature: string }) => {
  const { track } = useAnalytics();

  const {
    id: contentId,
    url_hash,
    url,
    propaganda_score: score,
    summary,
    source,
    authors,
    published_at,
    sentiment,
    techniques,
    confidence,
    scored_entity
  } = content;

  const scoreLabel = getScoreLabel(score);

  const renderSummaryButtons = () => {
    const buttonsMap = {
      manipulation_strategy: { title: "Manipulation Strategy", emoji: '🎯' },
      intended_audience: { title: "Intended Audience", emoji: '👥' },
      tone_analysis: { title: "Tone Analysis", emoji: '🎭' }
    }
    return Object.keys(buttonsMap).map((key) => {
      const popoverContent = content[key];
      if (!popoverContent) {
        return '';
      }
      const { title, emoji } = buttonsMap[key as keyof typeof buttonsMap];
      return (
        <Popover key={key}>
          <PopoverTrigger asChild>
            <Button variant="secondary" className="flex items-center gap-1" onClick={() => track('summary_button', { title })}>
              <span>{emoji}</span>
              <span>{title}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="mb-3 text-base font-semibold">{title}</div>
            <p>{popoverContent}</p>
          </PopoverContent>
        </Popover>
      )
    })
  }

  const overview = [
    { title: 'Source', content: <>{source.name} <Badge><ChartBarSquareIcon /> Avg. score: {(source.avg_score || 0).toFixed(2)}%</Badge></> },
    { title: 'Author(-s)', content: (
      <ul className="flex flex-col gap-1">
        {authors.map((item: any) => (
          <li key={item.author.id}>
            {item.author.name} {item.author.name !== 'Unknown' && <Badge><ChartBarSquareIcon /> Avg. score: {(item.author.avg_score || 0).toFixed(2)}%</Badge>}
          </li>
        ))}
      </ul>
    )},
    { title: 'Date published', content: published_at ? new Date(published_at).toLocaleString("en-US") : 'N/A' },
    { title: 'Overall sentiment', content: (
      <>
        {sentiment < -0.1 && <div className="flex items-center gap-1 text-red-700"><XCircleIcon className="size-4"/><span>Negative</span></div>}
        {sentiment < 0.1 && sentiment > -0.1 && <div className="text-muted-foreground flex items-center gap-1"><MinusCircleIcon className="size-4"/><span>Neutral</span></div>}
        {sentiment > 0.1 && <div className="text-muted-foreground flex items-center gap-1"><CheckCircleIcon className="text-green-700 size-4"/><span>Positive</span></div>}
      </>
    )},
    { title: 'Detection Confidence', content: `${(confidence * 100).toFixed(0)}%` },
  ]

  if (scored_entity) {
    overview.push({ title: 'Scored Entity', content: scored_entity ? getScoredEntityName(scored_entity) : 'N/A' });
  }

  return (
    <div className="py-10 px-6 w-full">
      <div className="flex items-center mb-8 justify-between gap-4 lg:gap-0">
        <h1 className="text-lg md:text-2xl lg:text-3xl font-bold whitespace-nowrap">Spin Analysis Report</h1>
        <div className="flex gap-2 md:gap-4 justify-end">
          <Button variant="link" asChild>
            <Link target="_blank" href={url} rel="noopener noreferrer">
              <ExternalLinkIcon /> <span className="hidden md:inline">Go To Original</span>
            </Link>
          </Button>
          <div className="flex gap-2 md:gap-4">
            <ShareDialog shareUrl={getUrl(`/reports/${url_hash}`)} />
            <FeedbackDialog contentId={contentId} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:gap-8">
        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8">
          <Card className="lg:w-1/3">
            <CardHeader>
              <CardTitle>Spin Score</CardTitle>
              <CardDescription>Indicates the level of manipulative, persuasive or emotionally loaded techniques found in the content</CardDescription>
            </CardHeader>
            <CardContent className="grow flex flex-col justify-center">
              <div className="flex justify-between items-center gap-3">
                <div className="flex items-end gap-1">
                  <span className="text-4xl lg:text-6xl font-bold" style={{ color: getTailwindGradientColor(score) }}>{score}</span>
                  <span className="text-muted-foreground">/</span>
                  <span className="text-muted-foreground">100</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm lg:text-base xl:text-lg font-semibold">
                    {scoreLabel.label}
                  </span>
                  <Popover>
                    <PopoverTrigger className="cursor-pointer"><InformationCircleIcon className="size-4"/></PopoverTrigger>
                    <PopoverContent className="text-sm">
                      {scoreLabel.description}
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="mt-3">
                <ScoreBar score={score} />
              </div>
            </CardContent>
            <CardFooter>
              <p className="text-sm text-muted-foreground">Higher scores suggest greater use of persuasive or biased tactics</p>
            </CardFooter>
          </Card>

          <Card className="lg:w-2/3">
            <CardHeader>
              <CardTitle>Content Analysis</CardTitle>
              <CardDescription>Reflection and interpretation of the content’s language and rhetorical structure</CardDescription>
            </CardHeader>
            <CardContent>
              <p>{summary}</p>
            </CardContent>
            <CardFooter className="flex flex-col lg:flex-row items-stretch gap-3">
              {renderSummaryButtons()}
            </CardFooter>
          </Card>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 lg:gap-8 items-stretch">
          <div className="flex flex-col gap-4 lg:gap-8 w-full lg:w-1/3">
            <Card>
              <CardHeader>
                <CardTitle>Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-gray-100">
                  {overview.map(({ title, content }) => (
                    <div key={title} className="py-4 sm:grid sm:grid-cols-5 sm:gap-4 sm:px-0">
                      <dt className="text-sm/6 font-medium col-span-2 text-gray-900">{title}</dt>
                      <dd className="mt-1 text-sm/6 text-gray-700 sm:col-span-3 sm:mt-0">
                        {content}
                      </dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            </Card>
            <IsHelpful contentId={contentId} signature={pageSignature} />
          </div>
          <Card className="w-full lg:w-2/3">
            <CardHeader>
              <CardTitle>Detected Techniques</CardTitle>
              <CardDescription>Manipulation and bias techniques detected in this content, based on language and framing</CardDescription>
            </CardHeader>
            <CardContent className="h-full">
              <div className="flex flex-wrap gap-2 h-full">
                {techniques.length > 0 ? (
                  <dl>
                    {techniques.map(({ technique, explanation, evidence }: any, index: number) => (
                      <div key={index}>
                        <dt className="font-semibold text-lg flex items-center gap-1">
                          <span>{getTechniqueEmoji(technique.name)}{technique.name}</span>
                          <Link target="_blank" rel="noopener noreferrer" href={getTechniqueUrl(technique.name)} aria-label="Learn more" className="text-blue-600 hover:underline">
                            <ExternalLinkIcon className="w-4 h-4" />
                          </Link>
                        </dt>
                        <dd className="mt-2 mb-4">
                          <span className="mr-2">{explanation}</span>
                          {evidence && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <Badge variant="secondary" className="cursor-pointer inline-flex items-center gap-1" onClick={() => track("evidence")}><span>Refs</span><FileTextIcon /></Badge>
                              </PopoverTrigger>
                              <PopoverContent className="prose prose-slate">
                                <blockquote>{evidence}</blockquote>
                              </PopoverContent>
                            </Popover>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                ): (
                  <div className="text-2xl text-muted-foreground flex flex-col justify-center items-center py-22 w-full font-light gap-2">
                    <span className="text-4xl">🕵🏻‍♂️</span>
                    <span>No techniques detected</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        <IsHelpful isBottom contentId={contentId} signature={pageSignature} />
      </div>
    </div>
  )
}

export default Report;
